/**
 * Keep `?task=project/slug` pointing at the task an open inspector shows
 * when that task's IDENTITY changes in place — renamed (new slug), moved to
 * another project, or deleted.
 *
 * The page that owns `?task=` (Tasks, Projects) follows the dock through
 * `useInspectorUrl`: a task panel disappearing reads as "the user closed it"
 * and clears the URL with a push. An identity change replaces the panel, so
 * without this the address bar would deny the inspector on screen, and Back
 * would land on a task id that no longer exists (CLAUDE.md deep-link rule:
 * the redirect after a destructive change is a replace).
 *
 * Every write here is a replace, and all of them happen in the same tick as
 * the panel change, so they compose with the page's own write into ONE
 * history entry (useUrlState's same-tick composition): the entry that opened
 * the task now names its new identity, or nothing after a delete.
 */
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useUrlPatch } from "../../hooks/useUrlState";
import { taskUrlId } from "../../lib/routes";

type TaskId = { project: string; slug: string };

export function useRetargetTaskUrl(): (from: TaskId, to: TaskId | null, apply: () => void) => void {
	const location = useLocation();
	const locationRef = useRef(location);
	locationRef.current = location;
	const patch = useUrlPatch();
	// An undo toast can fire after its page has gone: a write from an
	// unmounted caller would navigate the person back to where it last was.
	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	return useCallback(
		(from, to, apply) => {
			const named = new URLSearchParams(locationRef.current.search).get("task");
			if (!mounted.current || named !== taskUrlId(from.project, from.slug)) {
				// The address bar is not tracking this task (another one is open,
				// or this page does not own `?task=`): just change the panel.
				apply();
				return;
			}
			const next = to ? taskUrlId(to.project, to.slug) : null;
			// Before the panel change, so the owner's "panel removed → clear the
			// URL" write composes onto this replace instead of pushing…
			patch({ task: next }, { history: "replace" });
			apply();
			// …and after it, because that write just cleared the key again.
			if (next) patch({ task: next }, { history: "replace" });
		},
		[patch],
	);
}
